import { useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Select,
  Switch,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import AddIcon from '@mui/icons-material/Add'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import CloseIcon from '@mui/icons-material/Close'
import TitleOutlinedIcon from '@mui/icons-material/TitleOutlined'
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined'
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined'
import LinearScaleOutlinedIcon from '@mui/icons-material/LinearScaleOutlined'
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined'
import SortOutlinedIcon from '@mui/icons-material/SortOutlined'
import type { SvgIconComponent } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import OptionsEditor from '@/components/questions/OptionsEditor'
import { kindHasOptions, makeOption, makeQuestion, newId, type EditableQuestion, type QuestionKind } from '@/components/questions/types'
import { apiQuestionToEditable, type ApiQuestion } from '@/components/questions/apiAdapter'
import { projectService, type QuestionnaireTemplate } from '@/services/projectService'
import { DeskInput, Field, Group, Row, Segmented, StepHeading, Tag } from './desk'
import { LINE, LINE_SOFT, outlinedBtnSx, primaryBtnSx } from './deskStyles'
import type { DraftPatch, QuestionTiming, StudyDraft } from './types'

type StepProps = {
  draft: StudyDraft
  onChange: (patch: DraftPatch) => void
  templates: QuestionnaireTemplate[]
}

const WIZARD_KINDS: QuestionKind[] = ['scale', 'single', 'multi', 'text', 'nps', 'rank']

const KIND_ICON: Record<QuestionKind, SvgIconComponent> = {
  text: TitleOutlinedIcon,
  single: FormatListBulletedOutlinedIcon,
  multi: ChecklistOutlinedIcon,
  scale: LinearScaleOutlinedIcon,
  nps: StarBorderOutlinedIcon,
  rank: SortOutlinedIcon,
}

/** Ready-made respondent-metadata fields (metryczka). */
const PRESET_KEYS = ['gender', 'age', 'education', 'residence', 'income', 'employment'] as const
type PresetKey = (typeof PRESET_KEYS)[number]

export default function WizardStepQuestions({ draft, onChange, templates }: StepProps) {
  const c = useTheme().affexy.colors
  const { t } = useTranslation('dashboard')
  const tq = useTranslation('questions').t

  const [openId, setOpenId] = useState<string | null>(null)
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null)
  const [templatesOpen, setTemplatesOpen] = useState(false)

  const questions = draft.questions
  const setQuestions = (next: EditableQuestion[]) => onChange({ questions: next })
  const patch = (id: string, p: Partial<EditableQuestion>) => setQuestions(questions.map((q) => (q.id === id ? { ...q, ...p } : q)))
  const remove = (id: string) => {
    setQuestions(questions.filter((q) => q.id !== id))
    if (openId === id) setOpenId(null)
  }
  const add = (kind: QuestionKind) => {
    const q = makeQuestion(kind, false)
    setQuestions([...questions, q])
    setOpenId(q.id)
    setAddAnchor(null)
  }
  const changeKind = (q: EditableQuestion, kind: QuestionKind) =>
    patch(q.id, {
      kind,
      options: kindHasOptions(kind) ? (q.options.length ? q.options : [makeOption(0, false), makeOption(1, false)]) : [],
    })

  const applyTemplate = (id: number, incoming: EditableQuestion[], mode: 'replace' | 'append') => {
    onChange({ questions: mode === 'replace' ? incoming : [...questions, ...incoming], questionnaireTemplateId: id })
    setTemplatesOpen(false)
  }

  return (
    <>
      <StepHeading title={t('wizard.desk.questions.title')} lead={t('wizard.desk.questions.lead')} />

      <Group
        title={t('wizard.desk.questions.survey')}
        badge={questions.length > 0 ? <Tag>{questions.length}</Tag> : undefined}
        action={
          <>
            <Button onClick={() => setTemplatesOpen(true)} sx={outlinedBtnSx}>
              {t('wizard.desk.questions.fromTemplate')}
            </Button>
            <Button
              variant="contained"
              onClick={(e) => setAddAnchor(e.currentTarget)}
              startIcon={<AddIcon sx={{ fontSize: 18 }} />}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
              sx={primaryBtnSx}
              aria-haspopup="menu"
            >
              {t('wizard.desk.questions.add')}
            </Button>
          </>
        }
        flush
      >
        <Row>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 14.5, fontWeight: 600, flex: 1, minWidth: 120 }}>{t('wizard.desk.questions.when')}</Typography>
            <Segmented<QuestionTiming>
              label={t('wizard.desk.questions.when')}
              value={draft.questionTiming}
              onChange={(questionTiming) => onChange({ questionTiming })}
              options={[
                { value: 'start', label: t('wizard.desk.questions.before') },
                { value: 'end', label: t('wizard.desk.questions.after') },
              ]}
            />
          </Box>
        </Row>

        {questions.length === 0 ? (
          <Row>
            <Typography sx={{ py: 3, textAlign: 'center', color: c.ink56, fontSize: 14 }}>{t('wizard.desk.questions.empty')}</Typography>
          </Row>
        ) : (
          questions.map((q, i) => {
            const open = openId === q.id
            return (
              <Row key={q.id} sx={{ py: 0, bgcolor: open ? '#FAFAFC' : 'transparent', transition: 'background-color .15s ease' }}>
                <Box
                  component="button"
                  type="button"
                  aria-expanded={open}
                  onClick={() => setOpenId(open ? null : q.id)}
                  sx={{
                    font: 'inherit',
                    color: 'inherit',
                    textAlign: 'left',
                    width: '100%',
                    border: 0,
                    bgcolor: 'transparent',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    py: 1.75,
                    px: 0,
                    minWidth: 0,
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      bgcolor: '#F0F0F4',
                      color: c.ink72,
                      fontSize: 12,
                      fontWeight: 700,
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {i + 1}
                  </Box>
                  <Box component="span" sx={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: open ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {q.text.trim() || <Box component="span" sx={{ color: c.ink32 }}>{t('wizard.desk.questions.untitled')}</Box>}
                  </Box>
                  <Tag>{tq(`kinds.${q.kind}`)}</Tag>
                  <KeyboardArrowDownIcon sx={{ fontSize: 20, color: c.ink48, transition: 'transform .2s ease', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }} />
                </Box>
                {open && (
                  <Box sx={{ pb: 2.25, pl: { sm: 4.5 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Field label={t('wizard.questions.editText')}>
                      <DeskInput autoFocus={!q.text} value={q.text} onChange={(e) => patch(q.id, { text: e.target.value })} placeholder={tq('questionPlaceholder')} />
                    </Field>
                    <Field label={t('wizard.desk.questions.colType')}>
                      <Select
                        value={q.kind}
                        onChange={(e) => changeKind(q, e.target.value as QuestionKind)}
                        renderValue={(kind) => {
                          const Icon = KIND_ICON[kind]
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Icon sx={{ fontSize: 17, color: c.ink56 }} />
                              {tq(`kinds.${kind}`)}
                            </Box>
                          )
                        }}
                        sx={{
                          height: 42,
                          maxWidth: 280,
                          borderRadius: '10px',
                          bgcolor: '#fff',
                          fontSize: 14.5,
                          '& .MuiOutlinedInput-notchedOutline': { borderColor: LINE },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: c.coral, borderWidth: 1 },
                        }}
                      >
                        {WIZARD_KINDS.map((kind) => {
                          const Icon = KIND_ICON[kind]
                          return (
                            <MenuItem key={kind} value={kind} sx={{ fontSize: 14, gap: 1 }}>
                              <Icon sx={{ fontSize: 16, color: c.ink56 }} />
                              {tq(`kinds.${kind}`)}
                            </MenuItem>
                          )
                        })}
                      </Select>
                    </Field>
                    {kindHasOptions(q.kind) ? (
                      <OptionsEditor options={q.options} onChange={(options) => patch(q.id, { options })} marker={q.kind === 'single' ? 'radio' : 'check'} />
                    ) : q.kind === 'scale' || q.kind === 'nps' ? (
                      <Typography sx={{ fontSize: 13, color: c.ink56 }}>{q.kind === 'scale' ? tq('scaleHint') : tq('npsHint')}</Typography>
                    ) : null}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="label" sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', flex: 1 }}>
                        <Switch size="small" checked={q.required} onChange={(e) => patch(q.id, { required: e.target.checked })} />
                        <Typography sx={{ fontSize: 14 }}>{t('wizard.desk.questions.colRequired')}</Typography>
                      </Box>
                      <Button size="small" onClick={() => remove(q.id)} startIcon={<DeleteOutlineIcon sx={{ fontSize: 17 }} />} sx={{ color: c.danger, fontSize: 13.5, borderRadius: '8px' }}>
                        {t('wizard.desk.questions.delete')}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Row>
            )
          })
        )}
      </Group>

      <Menu
        anchorEl={addAnchor}
        open={!!addAnchor}
        onClose={() => setAddAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { mt: 0.75, width: 220, borderRadius: '12px', border: `1px solid ${LINE}`, boxShadow: '0 12px 32px -8px rgba(27,28,56,.25)' } } }}
      >
        {WIZARD_KINDS.map((kind) => {
          const Icon = KIND_ICON[kind]
          return (
            <MenuItem key={kind} onClick={() => add(kind)} sx={{ fontSize: 13.5, mx: 0.625, borderRadius: '6px' }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <Icon sx={{ fontSize: 16, color: c.ink56 }} />
              </ListItemIcon>
              {tq(`kinds.${kind}`)}
            </MenuItem>
          )
        })}
      </Menu>

      <MetricsSection items={draft.demographics} onChange={(demographics) => onChange({ demographics })} />

      <TemplatesDrawer
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        templates={templates}
        selectedId={draft.questionnaireTemplateId}
        existing={questions.length}
        onApply={applyTemplate}
      />
    </>
  )
}

/** Metryczka: one checkbox per preset field; the answers of a checked one can be edited. */
function MetricsSection({ items, onChange }: { items: EditableQuestion[]; onChange: (next: EditableQuestion[]) => void }) {
  const c = useTheme().affexy.colors
  const { t } = useTranslation('dashboard')
  const [editing, setEditing] = useState<string | null>(null)

  const keyOf = (q: EditableQuestion) => (q.section?.startsWith('meta:') ? q.section.slice(5) : null)
  const byKey = (key: PresetKey) => items.find((q) => keyOf(q) === key)
  // Fields from an older draft or a template that are not one of the presets.
  const others = items.filter((q) => !PRESET_KEYS.includes(keyOf(q) as PresetKey))

  const toggle = (key: PresetKey, on: boolean) => {
    if (!on) {
      onChange(items.filter((q) => keyOf(q) !== key))
      if (editing === key) setEditing(null)
      return
    }
    const options = t(`wizard.questions.metrics.presets.${key}.options`, { returnObjects: true }) as string[]
    onChange([
      ...items,
      {
        id: newId('meta'),
        kind: 'single',
        text: t(`wizard.questions.metrics.presets.${key}.label`),
        required: false,
        section: `meta:${key}`,
        options: options.map((text) => ({ id: newId('opt'), text })),
      },
    ])
  }

  const editingItem = editing ? items.find((q) => keyOf(q) === editing || q.id === editing) : undefined

  const metricRow = (id: string, label: string, on: boolean, toggle: (v: boolean) => void, editKey: string) => (
    <Row key={id}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ fontSize: 14.5, flex: 1, minWidth: 0 }}>{label}</Typography>
        {on && (
          <Box
            component="button"
            type="button"
            onClick={() => setEditing(editing === editKey ? null : editKey)}
            sx={{ border: 0, bgcolor: 'transparent', p: 0, cursor: 'pointer', font: 'inherit', fontSize: 13.5, color: editing === editKey ? c.coral : c.ink56, '&:hover': { color: c.coral } }}
          >
            {t('wizard.desk.questions.editAnswers')}
          </Box>
        )}
        <Switch checked={on} onChange={(e) => toggle(e.target.checked)} slotProps={{ input: { 'aria-label': label } }} />
      </Box>
      {on && editing === editKey && editingItem && (
        <Box sx={{ mt: 1.5 }}>
          <OptionsEditor
            options={editingItem.options}
            onChange={(options) => onChange(items.map((q) => (q.id === editingItem.id ? { ...q, options } : q)))}
            marker="radio"
          />
        </Box>
      )}
    </Row>
  )

  return (
    <Group title={t('wizard.questions.metrics.title')} description={t('wizard.desk.questions.metricsDesc')} flush>
      {PRESET_KEYS.map((key) => metricRow(key, t(`wizard.questions.metrics.presets.${key}.label`), !!byKey(key), (v) => toggle(key, v), key))}
      {others.map((q) => metricRow(q.id, q.text, true, () => onChange(items.filter((x) => x.id !== q.id)), q.id))}
    </Group>
  )
}

/** Side panel: pick a template, see its questions, then replace or append. */
function TemplatesDrawer({
  open,
  onClose,
  templates,
  selectedId,
  existing,
  onApply,
}: {
  open: boolean
  onClose: () => void
  templates: QuestionnaireTemplate[]
  selectedId: number | null
  existing: number
  onApply: (id: number, questions: EditableQuestion[], mode: 'replace' | 'append') => void
}) {
  const c = useTheme().affexy.colors
  const { t } = useTranslation('dashboard')
  const tq = useTranslation('questions').t
  const [picked, setPicked] = useState<{ id: number; questions: EditableQuestion[] } | null>(null)
  const [loadingId, setLoadingId] = useState<number | null>(null)
  const [loadError, setLoadError] = useState(false)

  const pick = async (id: number) => {
    if (loadingId !== null) return
    setLoadError(false)
    setLoadingId(id)
    try {
      const data = await projectService.getQuestionnaireTemplate(id)
      setPicked({ id, questions: ((data.questions ?? []) as unknown as ApiQuestion[]).map(apiQuestionToEditable) })
    } catch {
      setLoadError(true)
    } finally {
      setLoadingId(null)
    }
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 520 }, display: 'flex', flexDirection: 'column' } } }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2.5, py: 1.75, borderBottom: `1px solid ${LINE}` }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, flex: 1 }}>{t('wizard.desk.questions.fromTemplate')}</Typography>
        <IconButton size="small" onClick={onClose} aria-label={t('wizard.desk.close')}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Typography sx={{ fontSize: 13, color: c.ink56 }}>{t('wizard.questions.templatesDesc')}</Typography>
        {templates.length === 0 && <Typography sx={{ fontSize: 13.5, color: c.ink48, py: 2 }}>{t('wizard.questions.templatesEmpty')}</Typography>}
        {templates.map((tpl) => {
          const on = picked?.id === tpl.id || (!picked && tpl.id === selectedId)
          return (
            <Box
              key={tpl.id}
              component="button"
              type="button"
              onClick={() => void pick(tpl.id)}
              sx={{
                textAlign: 'left',
                font: 'inherit',
                cursor: 'pointer',
                bgcolor: '#fff',
                p: 1.5,
                borderRadius: '8px',
                border: `1px solid ${on ? c.coral : LINE}`,
                boxShadow: on ? `0 0 0 3px ${c.coralSoft}` : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                  {tpl.name}{' '}
                  <Box component="span" sx={{ fontWeight: 400, fontSize: 12.5, color: c.ink56 }}>
                    · {t('wizard.questions.templateQuestions', { count: tpl.questions_count })}
                  </Box>
                </Typography>
                {tpl.instructions && (
                  <Typography sx={{ fontSize: 12.5, color: c.ink56, mt: 0.25, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {tpl.instructions}
                  </Typography>
                )}
              </Box>
              {loadingId === tpl.id && <CircularProgress size={14} />}
            </Box>
          )
        })}
        {loadError && <Typography sx={{ fontSize: 12.5, color: c.danger }}>{t('wizard.questions.templateLoadFailed')}</Typography>}
        {picked && (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: c.ink48, mb: 0.5 }}>
              {t('wizard.desk.questions.inTemplate')}
            </Typography>
            {picked.questions.map((q, i) => (
              <Box key={q.id} sx={{ display: 'flex', gap: 1.5, py: 1, borderBottom: `1px solid ${LINE_SOFT}`, fontSize: 13.5 }}>
                <Box component="span" sx={{ color: c.ink48, width: 18, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>{i + 1}.</Box>
                <Box component="span" sx={{ flex: 1, minWidth: 0 }}>{q.text}</Box>
                <Tag>{tq(`kinds.${q.kind}`)}</Tag>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1.75, borderTop: `1px solid ${LINE}` }}>
        {existing > 0 && <Typography sx={{ fontSize: 12.5, color: c.ink56 }}>{t('wizard.desk.questions.youHave', { count: existing })}</Typography>}
        <Box sx={{ flex: 1 }} />
        {existing > 0 ? (
          <>
            <Button disabled={!picked} onClick={() => picked && onApply(picked.id, picked.questions, 'replace')} sx={outlinedBtnSx}>
              {t('wizard.questions.templateReplace')}
            </Button>
            <Button variant="contained" disabled={!picked} onClick={() => picked && onApply(picked.id, picked.questions, 'append')} sx={primaryBtnSx}>
              {t('wizard.questions.templateAppend')}
            </Button>
          </>
        ) : (
          <Button variant="contained" disabled={!picked} onClick={() => picked && onApply(picked.id, picked.questions, 'replace')} sx={primaryBtnSx}>
            {t('wizard.desk.questions.insert')}
          </Button>
        )}
      </Box>
    </Drawer>
  )
}
